import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Upload, Spin, message, UploadProps, Progress } from 'antd';
import {
  InboxOutlined,
  DeleteOutlined,
  CheckSquareOutlined,
  DownloadOutlined,
  FileTextOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  JobService,
  ResumeImproveJobStatus,
  ResumeImproveProgress,
  ResumeImproveProfile,
  ResumeImproveResult,
  ResumeImprovementItem,
  ResumeRecommendationItem,
  AuthenticityFlag,
} from './services/job.service';
import Swal from 'sweetalert2';
import PublicActionPage from '../components/PublicActionPage';
import PublicConsentCheckbox from '../components/PublicConsentCheckbox';
import { KofiRequiredError } from '../support/kofi.service';
import { useToolGate } from '../support/useToolGate';
import KofiSupportPanel from '../support/KofiSupportPanel';
import ToolRunAllowance from '../support/ToolRunAllowance';

const progressPercentByStatus: Record<ResumeImproveJobStatus, number> = {
  QUEUED: 15,
  PROCESSING: 55,
  COMPLETED: 100,
  FAILED: 0,
};

const CVUpgrade: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [improvedResume, setImprovedResume] = useState<Blob | null>(null);
  const [improvementSummary, setImprovementSummary] = useState<string | null>(
    null
  );
  const [improvements, setImprovements] = useState<ResumeImprovementItem[]>([]);
  const [recommendations, setRecommendations] = useState<
    ResumeRecommendationItem[]
  >([]);
  const [profile, setProfile] = useState<ResumeImproveProfile | null>(null);
  const [authenticityWarnings, setAuthenticityWarnings] = useState<
    AuthenticityFlag[]
  >([]);
  const [fileSpinner, setFileSpinner] = useState<boolean>(false);
  const [hasConsented, setHasConsented] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);
  const [progressMessage, setProgressMessage] = useState<string>(
    'Processing your resume...'
  );
  const [progressPercent, setProgressPercent] = useState<number>(0);
  const { status, kofiRequired, setKofiRequired, refresh, onRedeemed } =
    useToolGate();
  const { Dragger } = Upload;

  const resetResults = () => {
    setFile(null);
    setImprovedResume(null);
    setImprovementSummary(null);
    setImprovements([]);
    setRecommendations([]);
    setProfile(null);
    setAuthenticityWarnings([]);
    setProcessError(null);
    setFileSpinner(false);
    setProgressPercent(0);
    setProgressMessage('Processing your resume...');
    setHasConsented(false);
  };

  const handleProgress = (progress: ResumeImproveProgress) => {
    setProgressMessage(progress.message);
    setProgressPercent(progressPercentByStatus[progress.status] ?? 10);
  };

  const processFile = async (resume: File) => {
    if (!hasConsented) return;

    setFile(resume);
    setProcessError(null);
    setKofiRequired(null);
    setImprovedResume(null);
    setImprovementSummary(null);
    setImprovements([]);
    setRecommendations([]);
    setProfile(null);
    setAuthenticityWarnings([]);
    try {
      setFileSpinner(true);
      setProgressPercent(10);
      setProgressMessage('Uploading your resume...');
      const result: ResumeImproveResult = await JobService.ImproveResume(
        resume,
        handleProgress
      );
      setProgressPercent(100);
      setImprovedResume(result.blob);
      setImprovementSummary(result.summary ?? null);
      setImprovements(result.improvements);
      setRecommendations(result.recommendations);
      setProfile(result.profile ?? null);
      setAuthenticityWarnings(result.authenticityWarnings ?? []);
      setHasConsented(false);
      message.success('Your improved resume is ready.');
      void refresh();
    } catch (error: unknown) {
      if (error instanceof KofiRequiredError) {
        setKofiRequired(error);
        setFile(resume);
        return;
      }
      console.error('Resume processing error:', error);
      const serverMessage = error instanceof Error ? error.message.trim() : '';
      const friendly =
        serverMessage &&
        !serverMessage.startsWith('Error:') &&
        !/^\d{3}\s*-/.test(serverMessage)
          ? serverMessage
          : "We couldn't process that resume. Try a text-based PDF or DOCX under 5 MB. Scanned image PDFs need readable text.";
      setProcessError(friendly);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'error',
        title: friendly,
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.stopTimer);
          toast.addEventListener('mouseleave', Swal.resumeTimer);
        },
      });
      setImprovedResume(null);
      setImprovementSummary(null);
      setImprovements([]);
      setRecommendations([]);
      setProfile(null);
      setAuthenticityWarnings([]);
      setFile(null);
    } finally {
      setFileSpinner(false);
      setProgressPercent(0);
      setProgressMessage('Processing your resume...');
    }
  };

  const uploadResumeProps: UploadProps = {
    multiple: false,
    maxCount: 1,
    accept: '.pdf,.docx',
    beforeUpload(file) {
      const isPDF = file.type === 'application/pdf';
      const isDOCX =
        file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      if (!isPDF && !isDOCX) {
        message.error('Only PDF or DOCX files are allowed.');
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    async onChange(info) {
      if (info.file && info.file.status !== 'removed') {
        const rawFile = (info.file.originFileObj || info.file) as File;
        await processFile(rawFile);
      } else {
        setFile(null);
        setImprovedResume(null);
      }
    },
    onRemove() {
      resetResults();
    },
    disabled: fileSpinner || !hasConsented,
  };

  const downloadImprovedResume = () => {
    if (!improvedResume || !file) return;
    const url = window.URL.createObjectURL(improvedResume);
    const link = document.createElement('a');
    const originalName = file.name.replace(/\.[^/.]+$/, '');
    link.href = url;
    link.setAttribute('download', `${originalName}_ModernCv.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <PublicActionPage
      eyebrow="Improve Resume"
      title="A cleaner resume in a few minutes"
      description="Upload a PDF or DOCX. We return an ATS-friendlier layout plus a short note on what changed."
      icon={<FileTextOutlined />}
      steps={[
        {
          title: 'Upload your current resume',
          detail: 'PDF or DOCX works best.',
        },
        {
          title: 'Wait while we reformat it',
          detail: 'Usually 1–2 minutes — keep this tab open.',
        },
        {
          title: 'Download and review',
          detail: 'Get the file plus a summary of improvements.',
        },
      ]}
      asideNote="Your file stays on this page until you download or clear it. Nothing is posted publicly."
      formEyebrow="Resume upload"
      formTitle="Drop your resume here"
      formDescription="Accept the privacy terms, then upload a file. Processing starts as soon as it is accepted."
      footer={
        <p className="jo-body-muted mb-0 text-center text-sm">
          Looking for roles?{' '}
          <Link to="/jobs" className="public-inline-link">
            Find open jobs
          </Link>{' '}
          or set up{' '}
          <Link to="/subscribe" className="public-inline-link">
            Job Alerts
          </Link>
          .
        </p>
      }
    >
      <div className="mx-auto w-full max-w-xl">
        <ToolRunAllowance status={status} />
        {!improvedResume && (
          <div className="public-form-field mb-2">
            <span className="public-form-label" id="cv-upload-label">
              Resume file
            </span>
            <p className="public-form-hint mb-2">Supports PDF and DOCX.</p>
            <PublicConsentCheckbox
              id="resume-improver-consent"
              checked={hasConsented}
              disabled={fileSpinner}
              purpose="the processing of my resume to create an improved version"
              onChange={setHasConsented}
              className="mb-3"
            />
            <Dragger
              {...uploadResumeProps}
              showUploadList={false}
              className="public-upload-zone flex min-h-[220px] flex-col justify-center rounded-lg"
              aria-labelledby="cv-upload-label"
            >
              {fileSpinner ? (
                <div
                  className="flex w-full flex-col items-center gap-4 px-4"
                  role="status"
                  aria-live="polite"
                >
                  <Spin />
                  <Progress
                    percent={progressPercent}
                    status="active"
                    showInfo={false}
                    className="w-full max-w-sm"
                  />
                  <p className="jo-body-subtle mb-0 text-center text-sm">
                    {progressMessage}
                  </p>
                  <p className="jo-body-subtle mb-0 text-center text-sm">
                    AI improvement can take 1–2 minutes. Leave this page open.
                  </p>
                </div>
              ) : file ? (
                <>
                  <p
                    className="ant-upload-drag-icon"
                    style={{ marginBottom: 0 }}
                  >
                    <FileTextOutlined style={{ fontSize: '36px' }} />
                  </p>
                  <p className="mb-1">
                    <strong>{file.name}</strong>
                  </p>
                  <p className="jo-body-muted mb-2 text-sm">
                    Uploaded — processing will start automatically.
                  </p>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      resetResults();
                    }}
                  >
                    Remove file
                  </Button>
                </>
              ) : (
                <>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ fontSize: '36px' }} />
                  </p>
                  <p className="ant-upload-text">Drag & drop or browse</p>
                  <p className="ant-upload-hint">PDF or DOCX</p>
                </>
              )}
            </Dragger>
          </div>
        )}

        {kofiRequired && !fileSpinner && (
          <KofiSupportPanel
            required={kofiRequired}
            onRedeemed={(next) => {
              onRedeemed(next);
              if (file) {
                void processFile(file);
              }
            }}
          />
        )}

        {processError && !fileSpinner && (
          <div
            className="public-result-panel public-result-panel--error mt-4"
            role="alert"
          >
            <p className="mb-3 text-sm font-semibold">{processError}</p>
            <Button
              className="public-button-secondary"
              icon={<ReloadOutlined />}
              onClick={resetResults}
            >
              Try another file
            </Button>
          </div>
        )}

        {improvedResume && file && (
          <div
            className="public-result-panel public-result-panel--success mt-2"
            role="status"
            aria-live="polite"
          >
            <p className="jo-success-banner mb-1 text-center font-semibold">
              <CheckSquareOutlined className="mr-2" aria-hidden="true" />
              Your improved resume is ready
            </p>
            <p className="jo-body-muted mb-0 text-center text-sm">
              Source file: <strong>{file.name}</strong>
            </p>

            {(improvementSummary ||
              improvements.length > 0 ||
              recommendations.length > 0) && (
              <div className="mt-5 text-left">
                <h3 className="jo-heading mb-2 text-base font-black">
                  What we improved
                </h3>
                {profileLabel(profile) && (
                  <p className="jo-body-subtle mb-3 text-sm">
                    Feedback tailored for {profileLabel(profile)}.
                  </p>
                )}
                {improvementSummary && (
                  <p className="jo-body-subtle mb-4 text-sm leading-relaxed">
                    {improvementSummary}
                  </p>
                )}
                {improvements.length > 0 && (
                  <ul className="m-0 list-none space-y-3 p-0">
                    {improvements.map((item, index) => (
                      <li
                        key={`${item.section}-${index}`}
                        className="public-improvement-item"
                      >
                        <p className="mb-1 text-sm font-semibold text-[var(--jo-text)]">
                          {item.section}
                        </p>
                        <p className="jo-body-subtle mb-0 text-sm leading-relaxed">
                          {item.description}
                        </p>
                        {item.rationale && (
                          <p className="jo-body-muted mb-0 mt-1 text-sm leading-relaxed">
                            {item.rationale}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {recommendations.length > 0 && (
                  <div className="mt-5">
                    <h4 className="jo-heading mb-2 text-sm font-black">
                      What only you can add
                    </h4>
                    <ul className="m-0 list-none space-y-3 p-0">
                      {recommendations.map((item, index) => (
                        <li
                          key={`${item.section ?? 'recommendation'}-${index}`}
                          className="public-improvement-item"
                        >
                          {item.section && (
                            <p className="mb-1 text-sm font-semibold text-[var(--jo-text)]">
                              {item.section}
                            </p>
                          )}
                          <p className="jo-body-subtle mb-0 text-sm leading-relaxed">
                            {item.recommendation}
                          </p>
                          {item.rationale && (
                            <p className="jo-body-muted mb-0 mt-1 text-sm leading-relaxed">
                              {item.rationale}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {authenticityWarnings.length > 0 && (
              <div className="mt-5 text-left">
                <h3 className="jo-heading mb-2 text-base font-black">
                  Check these claims
                </h3>
                <ul className="m-0 list-none space-y-3 p-0">
                  {authenticityWarnings.map((flag, index) => (
                    <li
                      key={`${flag.type}-${index}`}
                      className="public-improvement-item"
                    >
                      {flag.excerpt && (
                        <p className="mb-1 text-sm font-semibold text-[var(--jo-text)]">
                          {flag.excerpt}
                        </p>
                      )}
                      <p className="jo-body-subtle mb-0 text-sm leading-relaxed">
                        {flag.why}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="jo-body-muted mb-0 mt-5 text-center text-sm">
              <Link to="/ats-check" className="public-inline-link">
                ATS check
              </Link>
              {' · '}
              <Link to="/resume-authenticity" className="public-inline-link">
                Authenticity check
              </Link>
            </p>

            <div className="public-result-actions mt-5">
              <Button
                type="primary"
                className="public-button-primary"
                icon={<DownloadOutlined />}
                onClick={downloadImprovedResume}
              >
                Download resume
              </Button>
              <Button
                className="public-button-secondary"
                icon={<ReloadOutlined />}
                onClick={resetResults}
              >
                Improve another
              </Button>
            </div>
          </div>
        )}
      </div>
    </PublicActionPage>
  );
};

function profileLabel(profile: ResumeImproveProfile | null): string {
  if (!profile) {
    return '';
  }
  return [profile.seniority, profile.targetRole]
    .filter((value) => Boolean(value && value.trim()))
    .join(' ')
    .trim();
}

export default CVUpgrade;
